import moment from 'moment-jalaali';
import i18n from '../../../i18n';
import { ITags } from 'store/providers/providerTypes';
import {
  IAnswerView,
  IFileAnswerView,
  IGridAnswerView,
  IGridQuestionView,
  IIndicatorAnswerView,
  IIndicatorQuestionView,
  IMultipleAnswerAnswerView,
  IMultipleAnswerField,
  IMultipleAnswerQuestionView,
  IMultipleChoiceAnswerView,
  IMultipleChoiceQuestionView,
  IQuestionsView,
  ITextAnswerView,
  ITextQuestionView,
} from 'store/survey/surveyTypes';
import { smartDateString } from 'utils/date';

const formatDurationAsText = (time: number): string => {
  const hour = Math.floor(time);
  const minutes = Math.round((time - hour) * 60);
  return `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const formatIndicatorValue = (
  value: number | undefined,
  unit: string | null | undefined
): string => {
  if (value === undefined || value === null || Number.isNaN(value)) return '';
  if (unit === i18n.t('hour', { ns: 'survey' })) {
    return formatDurationAsText(value);
  }
  return String(value);
};

const formatMultipleAnswerFieldValue = (
  value: string,
  field: IMultipleAnswerField
): string => {
  if (field.inputType === 'date' && Number(value) > 0) {
    return moment(Number(value)).format('jD jMMMM jYYYY');
  }
  return value;
};

const isTruthyConfigValue = (value: string | undefined) =>
  value?.toLowerCase() === 'true';

const isNewsComplementaryQuestion = (question: IQuestionsView) =>
  isTruthyConfigValue(
    question.fields?.find(
      (f) => f.name === 'conf_newsComplementoryQuestionForNews'
    )?.value
  );

const getAnswerForQuestion = (
  answers: { [key: number]: IAnswerView },
  questionId: number
): IAnswerView | undefined => {
  const direct = answers[questionId];
  if (direct && typeof direct === 'object' && 'questionId' in direct) {
    return direct;
  }
  return answers?.[questionId] ?? {};
};

const getAnswerAsText = (
  question: IQuestionsView,
  answer: IAnswerView | undefined,
  tags?: ITags['data']
): string => {
  if (!answer) return '';

  switch (question.type) {
    case 'text': {
      const textAnswer = answer as ITextAnswerView;
      if (!textAnswer.value) return '';
      const inputType = (question as ITextQuestionView).inputType;
      if (inputType === 'rich') {
        return textAnswer.value ?? '';
      }
      if (inputType === 'date') {
        return Number(textAnswer.value) > 0
          ? moment(Number(textAnswer.value)).format('jD jMMMM jYYYY')
          : '';
      }
      if (inputType === 'dateAndTime') {
        return Number(textAnswer.value) > 0
          ? smartDateString(+textAnswer.value, true, true, true)
          : '';
      }
      if (inputType === 'duration') {
        return Number(textAnswer.value) >= 0
          ? formatDurationAsText(Number(textAnswer.value))
          : '';
      }
      return textAnswer.value;
    }
    case 'multipleChoice': {
      const mcAnswer = answer as IMultipleChoiceAnswerView;
      const mcQuestion = question as IMultipleChoiceQuestionView;
      if (mcQuestion.useTags && mcAnswer.selected?.length) {
        return mcAnswer.selected
          .map((id) => tags?.[id]?.text ?? String(id))
          .join(', ');
      }
      const values =
        mcAnswer.values ?? (mcAnswer.value !== null ? [mcAnswer.value] : []);
      return mcQuestion.choiceList
        .filter((choice) => values.includes(choice.value))
        .map((choice) => choice.text)
        .join(', ');
    }
    case 'multipleAnswer': {
      const maAnswer = answer as IMultipleAnswerAnswerView;
      const maQuestion = question as IMultipleAnswerQuestionView;
      return (
        maAnswer.subAnswerList
          ?.map((subAnswer) =>
            maQuestion.questionFields
              .filter((field) => !field.hidden)
              .map((field) => {
                const item = subAnswer.values?.find(
                  (value) => value.key === field.key
                );
                const value = item?.value;
                if (!value) return '';
                const formatted = formatMultipleAnswerFieldValue(value, field);
                return field.title ? `${field.title}: ${formatted}` : formatted;
              })
              .filter(Boolean)
              .join(', ')
          )
          .filter(Boolean)
          .join('; ') ?? ''
      );
    }

    case 'grid': {
      const gridAnswer = answer as IGridAnswerView;
      const gridQuestion = question as IGridQuestionView;
      const choiceList = gridQuestion.choiceList;
      const parts = gridQuestion.parts;
      return (
        gridAnswer.subAnswers
          ?.map((subAnswer) => {
            const partTitle = parts?.find(
              (part) => part.value === subAnswer.subQuestionValue
            )?.text;
            const choiceText = choiceList.find(
              (choice) => choice.value === subAnswer.value
            )?.text;
            if (!partTitle && !choiceText) return '';
            return `${partTitle ?? subAnswer.subQuestionValue}: ${choiceText ?? subAnswer.value}`;
          })
          .filter(Boolean)
          .join(', ') ?? ''
      );
    }
    case 'indicator': {
      const indicatorAnswer = answer as IIndicatorAnswerView;
      const indicatorQuestion = question as IIndicatorQuestionView;
      const numeratorText = `${indicatorQuestion.numerator}: ${formatIndicatorValue(
        indicatorAnswer.numerator,
        indicatorQuestion.numeratorUnit
      )}`;
      if (indicatorQuestion.denominator) {
        const denominatorText = `${indicatorQuestion.denominator}: ${formatIndicatorValue(
          indicatorAnswer.denominator,
          indicatorQuestion.denominatorUnit
        )}`;
        return `${numeratorText}, ${denominatorText}`;
      }
      return numeratorText;
    }
    case 'file': {
      const fileAnswer = answer as IFileAnswerView;
      return fileAnswer.file?.properties?.displayName ?? '';
    }
    default:
      return answer.value?.toString() ?? '';
  }
};

export const buildNewsComplementaryContext = (
  questions: { [questionId: number]: IQuestionsView } | undefined,
  answers: { [key: number]: IAnswerView } | undefined,
  tags?: ITags['data']
): string => {
  if (!questions || !answers) return '';

  return Object.values(questions)
    .filter(isNewsComplementaryQuestion)
    .sort(
      (a, b) => (a.indexOrder ?? a.order ?? 0) - (b.indexOrder ?? b.order ?? 0)
    )
    .map((question) => {
      const answerText = getAnswerAsText(
        question,
        getAnswerForQuestion(answers, question.id),
        tags
      );
      if (!answerText) return '';
      return `${question.title}: ${answerText}`;
    })
    .filter(Boolean)
    .join('\n');
};
